import React, { useState } from 'react';
import { Upload, X, File, CheckCircle } from 'lucide-react';
import { API_BASE_URL } from './api/config';
import { sessionManager } from './api/sessionManager';

const UPLOAD_URL = `${API_BASE_URL}/upload.php`;

const FileUploadContent = () => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    addFiles(selectedFiles);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const addFiles = (newFiles) => {
    const filesWithMetadata = newFiles.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'uploading',
      progress: 0,
      uploadedAt: new Date().toLocaleString(),
      error: null,
    }));

    setFiles((prevFiles) => [...prevFiles, ...filesWithMetadata]);

    filesWithMetadata.forEach((fileMeta) => {
      uploadFile(fileMeta);
    });
  };

  const uploadFile = (fileMeta) => {
    const formData = new FormData();
    formData.append('image', fileMeta.file);
    
    // Append user_id for tracking who uploaded the file
    const user = sessionManager.getUser();
    if (user && user.id) {
      formData.append('user_id', user.id);
    }

    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable) {
        const progress = Math.round((e.loaded / e.total) * 100);
        setFiles((prevFiles) =>
          prevFiles.map((f) =>
            f.id === fileMeta.id ? { ...f, progress: Math.min(progress, 99) } : f
          )
        );
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        let responseData = {};
        try {
          responseData = JSON.parse(xhr.responseText);
        } catch (_) {}

        if (responseData.success === false) {
          setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.id === fileMeta.id
                ? { ...f, status: 'error', error: responseData.message || 'Upload failed' }
                : f
            )
          );
        } else {
          setFiles((prevFiles) =>
            prevFiles.map((f) =>
              f.id === fileMeta.id ? { ...f, progress: 100, status: 'completed' } : f
            )
          );
        }
      } else {
        let serverMessage = `Server error: ${xhr.status}`;
        try {
          const errData = JSON.parse(xhr.responseText);
          if (errData.message || errData.error) {
            serverMessage = errData.message || errData.error;
          }
        } catch (_) {
          if (xhr.responseText) serverMessage = xhr.responseText.substring(0, 200);
        }
        console.error('Upload error response:', xhr.responseText);
        setFiles((prevFiles) =>
          prevFiles.map((f) =>
            f.id === fileMeta.id
              ? { ...f, status: 'error', error: serverMessage }
              : f
          )
        );
      }
    });

    xhr.addEventListener('error', () => {
      setFiles((prevFiles) =>
        prevFiles.map((f) =>
          f.id === fileMeta.id
            ? { ...f, status: 'error', error: 'Network error — could not reach server' }
            : f
        )
      );
    });

    xhr.open('POST', UPLOAD_URL);
    const token = sessionManager.getToken();
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }
    xhr.send(formData);
  };

  const removeFile = (fileId) => {
    setFiles((prevFiles) => prevFiles.filter((f) => f.id !== fileId));
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-800">File Upload</h1>
        <p className="text-gray-600 mt-2">Upload documents, reports, or any files for your banking operations</p>
      </div>

      {/* Upload Area */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`border-2 border-dashed rounded-lg p-12 text-center transition-all mb-6 ${
          isDragging
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-300 bg-white hover:border-primary-500 hover:bg-primary-50'
        }`}
      >
        <Upload size={48} className="mx-auto text-primary-500 mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Upload Files</h2>
        <p className="text-gray-600 mb-4">Drag and drop your files here or click to browse</p>
        <input
          type="file"
          id="file-input"
          onChange={handleFileInput}
          className="hidden"
          multiple
        />
        <label
          htmlFor="file-input"
          className="inline-block px-6 py-3 bg-primary-500 text-white font-medium rounded-lg cursor-pointer hover:bg-primary-600 transition-colors"
        >
          Select Files
        </label>
      </div>

      {/* Files List */}
      {files.length > 0 && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Uploaded Files ({files.length})</h3>
          <div className="space-y-3">
            {files.map((file) => (
              <div
                key={file.id}
                className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-4 flex-1">
                  <div className="flex-shrink-0">
                    <File size={24} className="text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)} • {file.uploadedAt}</p>
                    {file.status === 'uploading' && (
                      <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-gradient-to-r from-primary-400 to-primary-500 h-2 rounded-full transition-all duration-300"
                          style={{ width: `${file.progress}%` }}
                        ></div>
                      </div>
                    )}
                    {file.status === 'error' && (
                      <p className="text-xs text-red-500 mt-1">{file.error}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-2">
                    {file.status === 'completed' && (
                      <CheckCircle size={20} className="text-success" />
                    )}
                    {file.status === 'uploading' && (
                      <div className="text-xs text-gray-500 font-medium">{file.progress}%</div>
                    )}
                    {file.status === 'error' && (
                      <div className="text-xs text-red-500 font-medium">Failed</div>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => removeFile(file.id)}
                  className="flex-shrink-0 ml-4 p-1 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <X size={20} className="text-red-500" />
                </button>
              </div>
            ))}
          </div>

          {/* Summary */}
          <div className="mt-6 bg-accent-50 border border-accent-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <CheckCircle size={20} className="text-accent-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-accent-900">Upload Summary</h4>
                <p className="text-sm text-accent-700 mt-1">
                  {files.filter((f) => f.status === 'completed').length} of {files.length} files uploaded successfully
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {files.length === 0 && (
        <div className="mt-8 text-center text-gray-500">
          <p>No files uploaded yet. Start by uploading files above.</p>
        </div>
      )}
    </>
  );
};

export default FileUploadContent;
